import { PropsWithChildren, useEffect, useState } from 'react'

import styled, { css } from 'styled-components'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'

const StyledToggleGroup = styled(ToggleGroupPrimitive.Root)`
  display: flex;
  align-items: center;
  filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2));
`

const Separator = styled.span<{ $leftRight: 'left' | 'right' }>`
  &:after {
    content: '';
    position: absolute;
    width: 1px;
    background: #c4c4c480;
    height: 140%;
    transform: translate(0, -50%);
    top: 50%;

    ${({ $leftRight }) =>
      $leftRight === 'left'
        ? 'right: 0'
        : $leftRight === 'right'
          ? 'left: 0'
          : ''};
  }
`

const StyledItem = styled(ToggleGroupPrimitive.Item)<{
  size: ToggleGroupProps['size']
}>`
  border-radius: 6px;
  border: unset;
  color: #4f31ff;
  background: #eeeeee;

  ${({ size }) => {
    if (size === 'sm') {
      return css`
        font-size: 10px;
        padding: 9px 0;
      `
    } else if (size === 'md') {
      return css`
        font-size: 12px;
        padding: 10px 0;
      `
    } else if (size === 'lg') {
      return css`
        font-size: 14px;
        padding: 14px 0;
      `
    }
  }}

  font-weight: 700;
  min-width: 180px;
  cursor: pointer;

  &:hover {
    background: #4f31ff !important;
    color: #ffffff !important;
  }

  &[data-state='on'] {
    background: #4f31ff;
    color: #ffffff;
    z-index: 1;

    ${Separator} {
      display: none;
    }
  }
`

const ToggleGroupItemWrap = styled.div`
  border-radius: 6px;
  overflow: hidden;
  background: #eeeeee;
  white-space: nowrap;

  &:hover {
    ${StyledItem} {
      background: #eeeeee;
      color: #4f31ff;
    }
  }
`

const ItemInnerWrap = styled.div`
  position: relative;
`

const ToggleGroup = StyledToggleGroup
const ToggleGroupItem = StyledItem

export interface ToggleGroupProps {
  defaultValue: string
  leftItem: {
    value: string
    label: string
  }
  middleItem?: {
    value: string
    label: string
  }
  rightItem: {
    value: string
    label: string
  }
  onValueChange: (value: string) => void
  size: 'sm' | 'md' | 'lg'
}

function ToggleGroupComponent({
  defaultValue,
  leftItem,
  middleItem,
  rightItem,
  onValueChange,
  size,
  ...props
}: PropsWithChildren<ToggleGroupProps>) {
  const [value, setValue] = useState(defaultValue)

  useEffect(() => {
    setValue(defaultValue)
    onValueChange(defaultValue)
  }, [defaultValue])

  const hasMoreThanTwoItems = leftItem && middleItem && rightItem
  const isMiddleItemSelected = value === middleItem?.value

  return (
    <div {...props}>
      <ToggleGroup
        type="single"
        value={value}
        onValueChange={(value) => {
          if (value) {
            setValue(value)
            onValueChange(value)
          }
        }}
        orientation="horizontal"
      >
        <ToggleGroupItemWrap>
          <ToggleGroupItem value={leftItem.value} size={size}>
            <ItemInnerWrap>
              {leftItem.label}{' '}
              {hasMoreThanTwoItems && !isMiddleItemSelected && (
                <Separator data-testid="separator" $leftRight="left" />
              )}
            </ItemInnerWrap>
          </ToggleGroupItem>
          {middleItem && (
            <ToggleGroupItem value={middleItem.value} size={size}>
              {middleItem.label}
            </ToggleGroupItem>
          )}
          <ToggleGroupItem value={rightItem.value} size={size}>
            <ItemInnerWrap>
              {rightItem.label}{' '}
              {hasMoreThanTwoItems && !isMiddleItemSelected && (
                <Separator data-testid="separator" $leftRight="right" />
              )}
            </ItemInnerWrap>
          </ToggleGroupItem>
        </ToggleGroupItemWrap>
      </ToggleGroup>
    </div>
  )
}

export default ToggleGroupComponent
